import { useState, useEffect } from "react";
import api, { downloadFile, API } from "@/lib/api";
import { toast } from "sonner";
import {
  Search, Calendar, Mic, FileText, FileDown, X, User, Clock, CheckCircle2, Send,
  AudioLines, Phone, Mail, MessageCircle, ClipboardList, Stethoscope, ExternalLink,
} from "lucide-react";

export default function PatientRegistry({ mode = "data", onOpenConsult }) {
  const isFollowups = mode === "followups";
  const pref = isFollowups ? "fu" : "pd";
  const [q, setQ] = useState("");
  const [date, setDate] = useState("");
  const [patients, setPatients] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const [drawerTab, setDrawerTab] = useState("details");

  const load = async () => {
    const params = { q: q || undefined };
    if (isFollowups) { params.upcoming = true; params.follow_up_date = date || undefined; }
    const r = await api.get("/patients", { params });
    setPatients(r.data);
  };
  useEffect(() => { load(); }, [q, date]); // eslint-disable-line

  useEffect(() => {
    if (!drawer) return;
    const onKey = (e) => { if (e.key === "Escape") setDrawer(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawer]);

  const openDrawer = async (pid, tab = "details") => {
    const r = await api.get(`/patients/${pid}`);
    setDrawer(r.data);
    setDrawerTab(tab);
  };

  const completeFollow = async (pid) => {
    await api.post(`/followups/${pid}/complete`);
    toast.success("Follow-up marked complete");
    load();
    setDrawer(null);
  };

  const sendReminder = async (pid) => {
    try {
      const r = await api.post(`/followups/${pid}/send-reminder`);
      if (r.data.sent) toast.success("Reminder sent");
      else if (r.data.reason === "twilio_not_configured") toast.warning("Add Twilio credentials to send real messages");
      else toast.error("Could not send: " + (r.data.reason || "unknown"));
    } catch { toast.error("Failed to send reminder"); }
  };

  const startConsult = (pid) => { onOpenConsult(pid); setDrawer(null); };

  return (
    <div className="space-y-5" data-testid={isFollowups ? "followups-tab" : "patient-data-tab"}>
      <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input data-testid={isFollowups ? "input-search-followup-id" : "input-search-patient"} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by Patient ID, name, phone or email…"
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30" />
        </div>
        {isFollowups && (
          <div className="relative flex items-center gap-2">
            <span className="text-xs text-slate-500 whitespace-nowrap hidden sm:inline">Follow-up date</span>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input data-testid="input-filter-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} title="Filter by follow-up date" className="pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30" />
            </div>
          </div>
        )}
        {(q || date) && <button data-testid="btn-clear-filter" onClick={() => { setQ(""); setDate(""); }} className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-500 hover:bg-slate-50">Clear</button>}
      </div>

      <div className="rounded-2xl bg-white border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#EBF3FB] text-slate-600 text-xs">
              <tr>
                <th className="text-left p-3 font-semibold">Patient ID</th>
                <th className="text-left p-3 font-semibold">Name</th>
                <th className="text-left p-3 font-semibold">Age/Sex</th>
                <th className="text-left p-3 font-semibold">Contact</th>
                <th className="text-left p-3 font-semibold">{isFollowups ? "Follow-up" : "Registered"}</th>
                <th className="text-left p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {patients.map((p) => (
                <tr key={p.patient_id} data-testid={`patient-row-${pref}-${p.patient_id}`} className="border-t border-slate-50 hover:bg-slate-50/60">
                  <td className="p-3" data-notranslate><button onClick={() => openDrawer(p.patient_id)} className="font-mono-id text-[#1E5AA8] font-semibold hover:underline">{p.patient_id}</button></td>
                  <td className="p-3 font-medium text-slate-800" data-notranslate>{p.name}</td>
                  <td className="p-3 text-slate-500" data-notranslate>{p.age}/{p.gender}</td>
                  <td className="p-3 text-slate-500" data-notranslate>{p.phone || p.whatsapp || p.email || "—"}</td>
                  <td className="p-3">
                    {isFollowups
                      ? (p.next_follow_up ? (p.follow_up_done ? <span className="text-xs text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Done</span> : <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Clock className="w-3 h-3" />{p.next_follow_up}</span>) : <span className="text-slate-300">—</span>)
                      : <span className="text-slate-400">{(p.created_at || "").slice(0, 10)}</span>}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <button data-testid={`btn-start-consult-${pref}-${p.patient_id}`} onClick={() => startConsult(p.patient_id)} title="Start consultation" className="p-2 rounded-lg hover:bg-[#EBF3FB] text-[#1E5AA8]"><Mic className="w-4 h-4" /></button>
                      <button data-testid={`btn-view-patient-${pref}-${p.patient_id}`} onClick={() => openDrawer(p.patient_id)} title="View history" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><User className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {patients.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-slate-400">{isFollowups ? "No upcoming follow-ups found." : "No patients found."}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {drawer && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="patient-drawer">
          <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" onClick={() => setDrawer(null)} />
          <div className="relative w-full max-w-2xl bg-[#FBFBF7] h-full overflow-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 z-10">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display text-xl font-bold text-slate-900" data-notranslate>{drawer.patient.name}</h3>
                  <p className="text-xs text-slate-400" data-notranslate><span className="font-mono-id text-[#1E5AA8]">{drawer.patient.patient_id}</span> · {drawer.patient.age}/{drawer.patient.gender}</p>
                </div>
                <button onClick={() => setDrawer(null)} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
              </div>
              <button data-testid="drawer-start-consult" onClick={() => startConsult(drawer.patient.patient_id)} className="mt-3 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#1E5AA8] text-white font-semibold hover:bg-[#123C73]"><Stethoscope className="w-4 h-4" /> Start Consult</button>
              <div className="mt-3 flex gap-1 border-b border-slate-100 -mb-4">
                <DrawerTab id="details" active={drawerTab} onClick={setDrawerTab} icon={User} label="Details" />
                <DrawerTab id="reports" active={drawerTab} onClick={setDrawerTab} icon={FileText} label={`Reports${drawer.reports?.length ? ` (${drawer.reports.length})` : ""}`} />
                <DrawerTab id="prescriptions" active={drawerTab} onClick={setDrawerTab} icon={ClipboardList} label={`Prescriptions${drawer.prescriptions?.length ? ` (${drawer.prescriptions.length})` : ""}`} />
              </div>
            </div>

            <div className="p-5 space-y-4">
              {drawerTab === "details" && (
                <div data-testid="drawer-details" className="space-y-3">
                  {drawer.patient.allergies?.length > 0 && (
                    <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-notranslate>Allergies: {drawer.patient.allergies.join(", ")}</div>
                  )}
                  {(drawer.patient.next_follow_up && !drawer.patient.follow_up_done) && (
                    <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm text-amber-800">Follow-up due: {drawer.patient.next_follow_up}</span>
                      <div className="flex gap-2">
                        <button data-testid="btn-send-reminder" onClick={() => sendReminder(drawer.patient.patient_id)} className="text-xs bg-[#1E5AA8] text-white px-3 py-1.5 rounded-lg inline-flex items-center gap-1"><Send className="w-3 h-3" /> Send reminder</button>
                        <button data-testid="btn-complete-followup" onClick={() => completeFollow(drawer.patient.patient_id)} className="text-xs bg-emerald-600 text-white px-3 py-1.5 rounded-lg">Mark complete</button>
                      </div>
                    </div>
                  )}
                  <div className="rounded-xl bg-white border border-slate-100 p-4 grid sm:grid-cols-2 gap-x-6 gap-y-2.5 text-sm" data-notranslate>
                    <Info icon={Phone} label="Phone" value={drawer.patient.phone} />
                    <Info icon={MessageCircle} label="WhatsApp" value={drawer.patient.whatsapp} />
                    <Info icon={Mail} label="Email" value={drawer.patient.email} />
                  </div>
                  <div className="rounded-xl bg-white border border-slate-100 p-4 space-y-2 text-sm">
                    <HistRow label="Drug history" value={drawer.patient.drug_history} />
                    <HistRow label="Medical history" value={drawer.patient.medical_history} />
                    <HistRow label="Surgical history" value={drawer.patient.surgical_history} />
                    <HistRow label="Family history" value={drawer.patient.family_history} />
                  </div>
                </div>
              )}

              {drawerTab === "reports" && (
                <div data-testid="drawer-reports" className="space-y-2">
                  {(!drawer.reports || drawer.reports.length === 0) && <p className="text-sm text-slate-400 py-6 text-center">No attached reports. Reports uploaded while booking appointments appear here.</p>}
                  {(drawer.reports || []).map((rep) => (
                    <a key={rep.file_id} href={`${API}/appointments/${rep.appt_id}/reports/${rep.file_id}`} target="_blank" rel="noreferrer" data-testid="drawer-report-link"
                      className="flex items-center justify-between rounded-xl bg-white border border-slate-100 p-3 hover:bg-[#EBF3FB] transition-colors">
                      <span className="inline-flex items-center gap-2 text-sm text-slate-700 truncate" data-notranslate><FileText className="w-4 h-4 text-[#1E5AA8] shrink-0" />{rep.filename}</span>
                      <span className="inline-flex items-center gap-2 text-xs text-slate-400 shrink-0">{rep.date}<ExternalLink className="w-3.5 h-3.5" /></span>
                    </a>
                  ))}
                </div>
              )}

              {drawerTab === "prescriptions" && (
                <div data-testid="drawer-prescriptions" className="space-y-3">
                  {drawer.prescriptions.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No prescriptions yet.</p>}
                  {drawer.prescriptions.map((rx) => (
                    <div key={rx.id} className="rounded-xl bg-white border border-slate-100 p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400">{(rx.created_at || "").slice(0, 10)}</span>
                        <div className="flex gap-1">
                          <button onClick={() => downloadFile(`/prescriptions/${rx.id}/export?format=pdf`, "post", {}, "opd.pdf")} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" title="PDF"><FileDown className="w-4 h-4" /></button>
                          <button onClick={() => downloadFile(`/prescriptions/${rx.id}/export?format=docx`, "post", {}, "opd.docx")} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" title="Word"><FileText className="w-4 h-4" /></button>
                        </div>
                      </div>
                      {rx.chief_complaints?.length > 0 && <p className="text-sm mt-1 text-slate-700" data-notranslate><b>Complaints:</b> {rx.chief_complaints.join(", ")}</p>}
                      {rx.medications?.length > 0 && <p className="text-sm text-slate-500 mt-1" data-notranslate><b>Rx:</b> {rx.medications.map((m) => m.name).join(", ")}</p>}
                      {rx.follow_up_date && <p className="text-xs text-amber-700 mt-1">Follow-up: {rx.follow_up_date}</p>}
                    </div>
                  ))}

                  {drawer.consultations?.some((c) => c.audio_id) && (
                    <>
                      <h4 className="font-semibold text-slate-700 pt-2 flex items-center gap-2"><AudioLines className="w-4 h-4 text-[#1E5AA8]" /> Saved Recordings</h4>
                      {drawer.consultations.filter((c) => c.audio_id).map((c) => (
                        <div key={c.id} className="rounded-xl bg-white border border-slate-100 p-3" data-testid="consultation-recording">
                          <p className="text-xs text-slate-400 mb-2">{(c.created_at || "").slice(0, 16).replace("T", " ")}</p>
                          <audio controls preload="none" className="w-full h-9" src={`${API}/consultations/${c.id}/audio`} />
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DrawerTab({ id, active, onClick, icon: Icon, label }) {
  const on = active === id;
  return (
    <button data-testid={`drawer-tab-${id}`} onClick={() => onClick(id)}
      className={`relative inline-flex items-center gap-1.5 px-3 py-2 text-sm whitespace-nowrap transition-colors ${on ? "text-[#1E5AA8] font-semibold" : "text-slate-500 hover:text-slate-800"}`}>
      <Icon className="w-4 h-4" /> {label}
      {on && <span className="absolute left-2 right-2 -bottom-px h-0.5 rounded-full bg-[#1E5AA8]" />}
    </button>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-slate-400 shrink-0" />
      <span className="text-slate-400 text-xs w-20 shrink-0">{label}</span>
      <span className="text-slate-700 truncate">{value || "—"}</span>
    </div>
  );
}

function HistRow({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="text-slate-400 text-xs w-28 shrink-0">{label}</span>
      <span className="text-slate-700" data-notranslate>{value || "—"}</span>
    </div>
  );
}
